import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';

function Login({ isLoading, onGoogleSuccess, user }) {
  const navigate = useNavigate();
  const [backendAvailable, setBackendAvailable] = useState(true);
  const [backendMessage, setBackendMessage] = useState('');
  const [oauthBusy, setOauthBusy] = useState(false);
  const [oauthError, setOauthError] = useState('');
  const [googleReady, setGoogleReady] = useState(false);
  const [googleClientId, setGoogleClientId] = useState('');
  const googleButtonRef = useRef(null);

  const verifyGoogleToken = useCallback(async (idToken) => {
    const response = await fetch('/api/google/verify', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ id_token: idToken }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || data?.details || 'Google token verification failed');
    }
    return data;
  }, []);

  const renderGoogleButton = useCallback((clientId) => {
    if (!window.google?.accounts?.id || !googleButtonRef.current || !clientId) {
      return;
    }

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: async (response) => {
        if (!response?.credential) {
          setOauthError('Google did not return a credential.');
          setOauthBusy(false);
          return;
        }

        try {
          setOauthBusy(true);
          setOauthError('');
          const result = await verifyGoogleToken(response.credential);
          if (onGoogleSuccess) {
            await onGoogleSuccess(result.user || result);
          }
          navigate('/app', { replace: true });
        } catch (error) {
          setOauthError(error?.message || 'Authentication failed.');
        } finally {
          setOauthBusy(false);
        }
      },
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    googleButtonRef.current.innerHTML = '';
    window.google.accounts.id.renderButton(googleButtonRef.current, {
      theme: 'outline',
      size: 'large',
      width: 420,
      text: 'continue_with',
      shape: 'pill',
      locale: 'en',
    });
    setGoogleReady(true);
  }, [navigate, onGoogleSuccess, verifyGoogleToken]);

  useEffect(() => {
    let cancelled = false;

    const checkBackend = async () => {
      try {
        const response = await fetch('/api/test-oauth', {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Backend unavailable');
        }

        const data = await response.json().catch(() => ({}));
        if (!data?.client_id) {
          throw new Error('Missing Google client id');
        }

        if (!cancelled) {
          setGoogleClientId(data.client_id);
          setBackendAvailable(true);
          setBackendMessage('');
        }
        return;
      } catch (error) {
      }

      if (!cancelled) {
        setBackendAvailable(false);
        setBackendMessage('Backend API is offline. Start Django on port 8000 before trying to log in.');
      }
    };

    checkBackend();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!backendAvailable || !googleClientId) {
      return;
    }

    if (window.google?.accounts?.id) {
      renderGoogleButton(googleClientId);
      return;
    }

    let script = document.getElementById('google-identity-script');
    if (!script) {
      script = document.createElement('script');
      script.id = 'google-identity-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    const onLoad = () => renderGoogleButton(googleClientId);
    script.addEventListener('load', onLoad);

    return () => {
      script.removeEventListener('load', onLoad);
    };
  }, [backendAvailable, googleClientId, renderGoogleButton]);

  if (user) {
    return (
      <div className="login-page-shell">
        <div className="login-page-orb login-page-orb-left" />
        <div className="login-page-orb login-page-orb-right" />
        <div className="login-page-card login-page-card-centered">
          <h1>You are already signed in</h1>
          <p className="login-page-subtitle">Your workspace is ready.</p>
          <div className="login-page-actions">
            <button className="hero-primary-btn" type="button" onClick={() => navigate('/app')}>
              Open Workspace
            </button>
            <button className="hero-secondary-btn" type="button" onClick={() => navigate('/')}>
              Back Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page-shell">
      <div className="login-page-orb login-page-orb-left" />
      <div className="login-page-orb login-page-orb-right" />

      <div className="login-page-card login-page-card-centered login-page-card-compact">
        <button className="login-back-link" type="button" onClick={() => navigate('/')}>
          <ArrowBackRoundedIcon fontSize="small" />
          Back to Home
        </button>

        <div className="login-brand-mark" aria-hidden="true">∅</div>
        <div className="login-copy-badge">Google Login</div>
        <h1 className="login-page-title">Continue with Google</h1>
        <p className="login-page-subtitle">
          Sign in with your Google account to open the redaction workspace and start redacting your files.
        </p>

        {!backendAvailable ? (
          <div className="login-backend-warning" role="alert">
            {backendMessage}
          </div>
        ) : null}

        {oauthError ? (
          <div className="login-backend-warning" role="alert">
            {oauthError}
          </div>
        ) : null}

        <div className="login-page-actions login-page-actions-stacked">
          <div className="login-google-rendered" ref={googleButtonRef} />
          {!googleReady && backendAvailable ? (
            <button className="login-google-btn" type="button" disabled>
              <span className="login-google-btn-mark" aria-hidden="true">G</span>
              <span className="login-google-btn-label">Loading Google Sign-In...</span>
              <ArrowOutwardRoundedIcon fontSize="small" />
            </button>
          ) : null}
        </div>

        {(isLoading || oauthBusy) ? <p className="login-loading-copy">Signing you in...</p> : null}
      </div>
    </div>
  );
}

export default Login;
